import { logger } from '../lib/logger.js'

/**
 * CENTRALIZED CONFIGURATION
 * DO NOT HARDCODE THESE VALUES ANYWHERE ELSE
 *
 * All WebSocket URLs, model names, and other configuration values
 * must be imported from this file.
 */

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '')

const normalizeWebsocketUrl = (
  rawValue: string | undefined,
  {
    fallback,
    enforceSecure = false,
  }: {
    fallback: string
    enforceSecure?: boolean
  }
) => {
  if (!rawValue) {
    return trimTrailingSlash(fallback)
  }

  try {
    const trimmed = rawValue.trim()
    const hasScheme = /^[a-z]+:\/\//i.test(trimmed)
    const baseProtocol = enforceSecure ? 'wss://' : 'ws://'
    const candidate = hasScheme ? trimmed : `${baseProtocol}${trimmed}`
    const url = new URL(candidate)

    if (url.protocol === 'http:') url.protocol = 'ws:'
    if (url.protocol === 'https:') url.protocol = 'wss:'
    if (enforceSecure && url.protocol !== 'wss:') {
      url.protocol = 'wss:'
    }

    const hostname = url.hostname.toLowerCase()
    const isLocalHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' ||
      hostname.endsWith('.local') ||
      hostname.endsWith('.localdomain')

    if (!isLocalHost && url.protocol === 'wss:') {
      if (url.port && url.port !== '443') {
        logger.warn(
          `[WEBSOCKET_CONFIG] Stripping unsupported secure port "${url.port}" from ${url.hostname}`
        )
        url.port = ''
      }
    }

    // Remove pathname for WebSocket URLs (WebSocket doesn't use paths in the URL)
    // This ensures we get clean URLs like wss://host.com not wss://host.com/
    url.pathname = ''
    url.search = ''
    url.hash = ''

    // Build clean URL manually to avoid trailing slash issues
    const cleanUrl = `${url.protocol}//${url.hostname}${url.port ? `:${url.port}` : ''}`
    return trimTrailingSlash(cleanUrl)
  } catch (error) {
    logger.warn('[WEBSOCKET_CONFIG] Invalid WebSocket URL provided; falling back to default', {
      error: String(error),
    })
    return trimTrailingSlash(fallback)
  }
}

// WebSocket Configuration
// Check if we're running locally (client-side aware)
const isLocalhostRuntime = () => {
  // Server-side: check NODE_ENV
  if (typeof window === 'undefined') {
    return process.env.NODE_ENV !== 'production'
  }

  // Client-side: check hostname to detect localhost
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.local') ||
    hostname.endsWith('.localdomain') ||
    hostname.includes('.local')
  )
}

// Check if we're in production at runtime (client-side aware)
export const isProductionRuntime = () => {
  // Always use localhost when running locally, even if LIVE_SERVER_URL is set
  if (isLocalhostRuntime()) {
    return false
  }

  // If NEXT_PUBLIC_LIVE_SERVER_URL is explicitly set and we're not on localhost, use it
  if (process.env.NEXT_PUBLIC_LIVE_SERVER_URL) {
    return true
  }

  // Server-side: use NODE_ENV
  if (typeof window === 'undefined') {
    return process.env.NODE_ENV === 'production'
  }

  // Client-side: production if not localhost (includes vercel.app, custom domains, etc.)
  return true // We already checked for localhost above
}

export const WEBSOCKET_CONFIG = {
  LIVE_GATEWAY_PATH: '/live-gateway',
  // Distinct envs for prod vs dev to avoid accidental overrides
  // Note: process.env.* is replaced by Vite's define at build time (matches v9 approach)
  PRODUCTION_URL: normalizeWebsocketUrl(process.env.NEXT_PUBLIC_LIVE_SERVER_URL, {
    fallback: 'wss://fb-consulting-websocket.fly.dev',
    enforceSecure: true,
  }),
  DEVELOPMENT_URL: normalizeWebsocketUrl(process.env.NEXT_PUBLIC_LIVE_SERVER_DEV_URL, {
    fallback: 'ws://localhost:3001',
  }),
  get URL() {
    // Always use localhost when running locally, even if NEXT_PUBLIC_LIVE_SERVER_URL is set
    if (isLocalhostRuntime()) {
      // Prefer explicit dev URL when present
      if (process.env.NEXT_PUBLIC_LIVE_SERVER_DEV_URL) {
        logger.debug('[WEBSOCKET_CONFIG] Using dev URL from NEXT_PUBLIC_LIVE_SERVER_DEV_URL:', {
          url: this.DEVELOPMENT_URL,
        })
        return this.DEVELOPMENT_URL
      }

      // Derive from current host in the browser for local networks
      if (typeof window !== 'undefined') {
        const host = window.location.hostname
        const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:'
        const protocol = isSecure ? 'wss' : 'ws'
        // WebSocket server runs on 3001, NOT the same port as Vite (3000)
        const port = process.env.NEXT_PUBLIC_LIVE_SERVER_DEV_PORT ?? '3001'
        const portSuffix = port ? `:${port}` : ''
        const devUrl = `${protocol}://${host}${portSuffix}`
        logger.debug('[WEBSOCKET_CONFIG] Using dev URL (local development):', { url: devUrl })
        return devUrl
      }

      logger.debug('[WEBSOCKET_CONFIG] Using default dev URL (local development):', {
        url: this.DEVELOPMENT_URL,
      })
      return this.DEVELOPMENT_URL
    }

    // Production mode - use NEXT_PUBLIC_LIVE_SERVER_URL if set, otherwise fallback
    if (process.env.NEXT_PUBLIC_LIVE_SERVER_URL) {
      logger.debug('[WEBSOCKET_CONFIG] Using production URL from NEXT_PUBLIC_LIVE_SERVER_URL:', {
        url: this.PRODUCTION_URL,
      })
      return this.PRODUCTION_URL
    }

    // Production fallback
    logger.debug('[WEBSOCKET_CONFIG] Using production URL (fallback):', {
      url: this.PRODUCTION_URL,
    })
    return this.PRODUCTION_URL
  },
  RECONNECT_DELAY: 3000,
  MAX_RECONNECT_ATTEMPTS: 5,
  HEARTBEAT_INTERVAL: 30000, // 30 seconds
  MAX_BUFFERED_AMOUNT: 500_000, // 500KB - buffer threshold for client
  HEARTBEAT_TIMEOUT_MS: 45000, // Allow 45 seconds to accommodate server heartbeat interval
  FRAME_QUEUE_MAX_SIZE: 5,
  FRAME_QUEUE_PROCESS_INTERVAL: 500, // ms
  LOW_QUALITY_JPEG: 0.75,
  HIGH_BUFFER_THRESHOLD: 300_000, // 300KB - start reducing quality
} as const

// Gemini Model Names
//
// TWO PIPELINES. DO NOT COLLAPSE THEM.
// - REST / agents / research / PDF / vision-after-snapshot → Flash (generateContent)
// - Live voice + webcam + screenshare WebSocket → Live-only model (BidiGenerateContent)
// gemini-3.7-flash is REST-only (text output, no Live API). Pointing DEFAULT_LIVE
// at it kills native audio, VAD, barge-in, and realtime vision.
// gemini-3.8-live is the stable Live model. GEMINI_3_1_FLASH_LIVE stays for rollback.
const FLASH_LIVE_2026_03 = 'gemini-3.1-flash-live-preview'
const GEMINI_3_8_LIVE = 'gemini-3.8-live'
const GEMINI_3_5_FLASH = 'gemini-3.5-flash'
const GEMINI_3_7_FLASH = 'gemini-3.7-flash'
const AUDIO_2025_09 = GEMINI_3_8_LIVE
const AUDIO_2025_12 = GEMINI_3_8_LIVE

/** @deprecated Use DEFAULT_FAST or DEFAULT_CHAT from GEMINI_MODELS instead of specific versioned models */
export const GEMINI_MODELS = {
  // REST Flash workhorse (Aug 2026). Live is GEMINI_3_8_LIVE.
  GEMINI_3_7_FLASH,
  /** Rollback alias. Do not point DEFAULT_LIVE at this. */
  GEMINI_3_5_FLASH,

  // NEW: Google's latest REST models (auto-update to newest)
  FLASH_LATEST: GEMINI_3_7_FLASH, // REST Gemini 3.7 Flash
  FLASH_LITE_LATEST: 'gemini-3.1-flash-lite', // Stable Flash-Lite

  // Specific versions (predictable behavior)
  GEMINI_3_FLASH: GEMINI_3_7_FLASH, // Alias — preview → 3.5 GA → 3.7
  /** Stable Live API model (Sep 2026). */
  GEMINI_3_8_LIVE,
  /** Rollback only. Do not point DEFAULT_LIVE here. */
  GEMINI_3_1_FLASH_LIVE: FLASH_LIVE_2026_03,
  /** @deprecated Shut down Sep 2025. Use FLASH_LATEST or DEFAULT_FAST instead */
  FLASH_2025_09: GEMINI_3_5_FLASH,
  /** @deprecated Shut down Sep 2025. Use FLASH_LITE_LATEST or DEFAULT_FAST instead */
  FLASH_LITE_2025_09: 'gemini-3.1-flash-lite',
  /** @deprecated Legacy Live API audio model. Use DEFAULT_LIVE instead */
  AUDIO_2025_09, // DEPRECATED: shut down — redirected to DEFAULT_LIVE
  /** @deprecated Legacy Live API audio model. Use DEFAULT_LIVE instead */
  AUDIO_2025_12, // DEPRECATED: redirected to DEFAULT_LIVE

  // Gemini 3 Pro family
  GEMINI_3_1_PRO_PREVIEW: 'gemini-3.1-pro-preview',
  /** @deprecated Ambiguous legacy alias. Use GEMINI_3_1_PRO_PREVIEW for Pro or DEFAULT_CHAT for normal chat. */
  GEMINI_3_PRO_PREVIEW: 'gemini-3.1-pro-preview',

  // LEGACY: For backward compatibility (deprecated models)
  /** @deprecated Old experimental model. Use DEFAULT_FAST instead */
  FLASH_EXP: 'gemini-3.1-flash-lite', // DEPRECATED: shut down — redirected to FLASH_LITE_LATEST

  // DEFAULTS: What each use case should use
  DEFAULT_CHAT: GEMINI_3_7_FLASH, // REST text / agents / Google Search
  DEFAULT_LIVE: GEMINI_3_8_LIVE, // Live voice + realtime vision — NEVER a REST Flash ID
  DEFAULT_VISION: GEMINI_3_5_FLASH, // REST snapshot vision — stay on 3.5
  DEFAULT_AUDIO: GEMINI_3_8_LIVE, // Live audio processing
  DEFAULT_VOICE: GEMINI_3_8_LIVE, // Live voice model
  DEFAULT_MULTIMODAL: GEMINI_3_5_FLASH, // REST snapshot vision
  DEFAULT_WEBCAM: GEMINI_3_5_FLASH, // REST webcam snapshot analysis
  FALLBACK: GEMINI_3_7_FLASH, // REST fallback (circuit breaker still uses DEFAULT_RELIABLE)
  DEFAULT_SCREEN: GEMINI_3_5_FLASH, // REST screen snapshot analysis
  DEFAULT_FAST: GEMINI_3_7_FLASH, // Quick / off-topic / discovery / search
  DEFAULT_RELIABLE: 'gemini-3.1-flash-lite', // Fallback when primary fails (stable, cheap)
} as const

// Gemini API Endpoints
export const GEMINI_ENDPOINTS = {
  LIVE_API: 'generativelanguage.googleapis.com/v1beta/models',
  STANDARD_API: 'generativelanguage.googleapis.com/v1/models',
  STREAMING_API: 'generativelanguage.googleapis.com/v1beta/models',
  V1_ALPHA_API: 'generativelanguage.googleapis.com/v1alpha/models', // Required for Gemini 3 features like media_resolution
} as const

// Embedding Models — env override for instant rollback without code change
export const EMBEDDING_MODELS = {
  DEFAULT: (process.env.EMBEDDING_MODEL ?? 'gemini-embedding-2') as
    | 'gemini-embedding-2'
    | 'gemini-embedding-2-preview'
    | 'gemini-embedding-001',
  GEMINI_EMBEDDING_2: 'gemini-embedding-2',
  /** @deprecated Preview alias retained only for env rollback. Use GEMINI_EMBEDDING_2. */
  GEMINI_EMBEDDING_2_PREVIEW: 'gemini-embedding-2-preview',
  GEMINI_EMBEDDING_001: 'gemini-embedding-001',
  EMBEDDING_001: 'embedding-001',
  TEXT_EMBEDDING_004: 'text-embedding-004',
  TEXT_EMBEDDING_005: 'text-embedding-005',
} as const

// Gemini 3.0 Specific Configurations (per https://ai.google.dev/gemini-api/docs/thinking)
export const THINKING_LEVELS = {
  MINIMAL: 'minimal', // 3.5 Flash / Flash-Lite. 3.7 remaps MINIMAL→LOW in ai-client.
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high', // Default for Gemini 3
} as const

export const THOUGHT_SIGNATURES = {
  // Discovery Agent: Systematic, inquisitive, comprehensive
  DISCOVERY:
    'Systematic analysis of user needs, covering: goals, pain points, data, readiness, budget, success metrics.',

  // Technical Analysis: Deep code/architecture review
  TECHNICAL:
    'Deep technical evaluation of architecture, scalability, security, and integration patterns.',

  // Creation/Drafting: Creative but structured
  CREATIVE:
    'Structured creative process: verified facts > clear structure > engaging tone > refinement.',

  // Problem Solving: Step-by-step logic
  LOGICAL:
    'Chain-of-thought problem solving: Identify core issue -> Break down components -> Analyze root causes -> Propose solutions -> Verify.',
} as const

export const MEDIA_RESOLUTIONS = {
  LOW: 'media_resolution_low',
  MEDIUM: 'media_resolution_medium',
  HIGH: 'media_resolution_high',
} as const

// Gemini Live API Configuration
export const LIVE_API_CONFIG = {
  // Use sendRealtimeInput(), NOT session.send()
  METHOD_NAME: 'sendRealtimeInput',
  AUDIO_ENCODING: 'pcm_s16le',
  SAMPLE_RATE: 24000,
  CHANNELS: 1,
} as const

// Type safety
export type GeminiModel = (typeof GEMINI_MODELS)[keyof typeof GEMINI_MODELS]
export type GeminiEndpoint = (typeof GEMINI_ENDPOINTS)[keyof typeof GEMINI_ENDPOINTS]

// API Rate Limits
export const RATE_LIMITS = {
  WEBCAM_CAPTURE_INTERVAL: 2000, // 2 seconds (for analysis, not streaming)
  SCREEN_CAPTURE_INTERVAL: 2000, // 2 seconds (for analysis, not streaming)
  // Live API streaming intervals - much longer to avoid overwhelming the API
  WEBCAM_STREAM_INTERVAL: 1000, // 1 second - faster for real-time feel
  SCREEN_STREAM_INTERVAL: 1000, // 1 second - faster for real-time feel
} as const

export const MEDIA_CAPTURE_CONFIG = {
  CAMERA_START_TIMEOUT_MS: 15_000,
  /** One retry after timeout when permission prompt is slow (incognito / first grant). */
  CAMERA_START_MAX_ATTEMPTS: 2,
  // getDisplayMedia() shows a browser picker dialog where the user selects a
  // source and clicks "Share". The dialog can stay open indefinitely — a 15s
  // timeout was too aggressive and caused timeouts when users took time to
  // choose. 60s matches real-world interaction time.
  SCREEN_SHARE_START_TIMEOUT_MS: 60_000,
  SCREEN_SHARE_FRAME_READY_TIMEOUT_MS: 8_000,
  /** Target first Live screen frame after share starts (SLO for Item 17). */
  SCREEN_SHARE_FIRST_FRAME_TARGET_MS: 3_000,
  /** Escalate silent Live drops once first frame still undelivered past this. */
  SCREEN_SHARE_FIRST_FRAME_WARN_MS: 3_000,
  START_FAILURE_RETRY_COOLDOWN_MS: 30_000,
  VISUAL_FRAME_SKIP_LOG_INTERVAL_MS: 5_000,
} as const

// Context Configuration
export const CONTEXT_CONFIG = {
  REDIS_TTL: 3600, // 1 hour for active sessions
  ARCHIVE_ON_DISCONNECT: true,
  AUTO_GENERATE_PDF: true,
  MIN_MESSAGES_FOR_ARCHIVE: 3, // Don't archive test conversations
  SUMMARIZE_THRESHOLD: 50, // Summarize every 50 messages
} as const

// Security Configuration
export const SECURITY_CONFIG = {
  ENABLE_PII_DETECTION: process.env.NODE_ENV === 'production',
  ENABLE_PII_REDACTION: process.env.NODE_ENV === 'production',
  ENABLE_AUDIT_LOGGING: true,
  DATA_RETENTION_DAYS: 90, // GDPR compliance
  ENABLE_ENCRYPTION_AT_REST: true, // For Supabase
} as const

// Session Configuration
export const SESSION_CONFIG = {
  TIMEOUT: 30 * 60 * 1000, // 30 minutes
  WARNING_THRESHOLD: 25 * 60 * 1000, // 25 minutes
  HEARTBEAT_INTERVAL: 60 * 1000, // 1 minute
} as const

// Audio Configuration
export const AUDIO_CONFIG = {
  SAMPLE_RATE: 24000,
  CHANNELS: 1,
  BIT_DEPTH: 16,
  CHUNK_SIZE: 4096,
  NOISE_GATE_THRESHOLD: -50, // dB
} as const

// Security / CORS
export const ALLOWED_ORIGINS = (
  process.env.NEXT_PUBLIC_ALLOWED_ORIGINS ||
  [
    'https://fbcai.com',
    'https://farzadbayat.com',
    'https://www.farzadbayat.com',
    'http://localhost:3000',
    'http://localhost:3001',
    'http://127.0.0.1:3000',
    'http://127.0.0.1:3001',
  ].join(',')
)
  .split(',')
  .map(s => s.trim())
  .filter(Boolean)

// Helper to check if origin is allowed (includes Vercel pattern matching)
export const isOriginAllowed = (origin: string | undefined | null): boolean => {
  if (!origin || origin === 'null' || origin === '') {
    // Allow empty origin for some WebSocket clients that don't send origin header
    // This is less secure but necessary for compatibility
    return true
  }

  // Check exact match in allowed origins
  if (ALLOWED_ORIGINS.includes(origin)) {
    return true
  }

  // Allow any Vercel deployment (*.vercel.app)
  if (origin.includes('.vercel.app')) {
    return true
  }

  return false
}

// Third-party API endpoints
export const EXTERNAL_ENDPOINTS = {
  RESEND_EMAIL: process.env.NEXT_PUBLIC_RESEND_API_ENDPOINT || 'https://api.resend.com/emails',
  PERPLEXITY_CHAT_COMPLETIONS:
    process.env.NEXT_PUBLIC_PERPLEXITY_API_ENDPOINT || 'https://api.perplexity.ai/chat/completions',
} as const

// Contact details & scheduling configuration
const schedulingUsername = process.env.NEXT_PUBLIC_SCHEDULING_USERNAME || 'farzad-bayat'
const schedulingEvent = process.env.NEXT_PUBLIC_SCHEDULING_EVENT || '30min'
const schedulingBaseUrl = trimTrailingSlash(
  process.env.NEXT_PUBLIC_SCHEDULING_BASE_URL || 'https://cal.com'
)
const schedulingEmbedBaseUrl = trimTrailingSlash(
  process.env.NEXT_PUBLIC_SCHEDULING_EMBED_BASE_URL || 'https://app.cal.com'
)
const schedulingEmbedScript =
  process.env.NEXT_PUBLIC_SCHEDULING_EMBED_SCRIPT || 'https://app.cal.com/embed/embed.js'
const contactEmailEnvName = ['NEXT', 'PUBLIC', 'CONTACT', 'EMAIL'].join('_')
const defaultSupportEmail = process.env[contactEmailEnvName] || 'contact@farzadbayat.com'
const contactNotificationEmailSource =
  process.env.CONTACT_NOTIFICATION_EMAILS ||
  process.env.CONTACT_NOTIFICATION_EMAIL ||
  [
    process.env.RESEND_TO_EMAIL,
    defaultSupportEmail,
    'bayatfarzad@gmail.com',
  ]
    .filter(Boolean)
    .join(',')

export const CONTACT_CONFIG = {
  SUPPORT_EMAIL: defaultSupportEmail,
  NOTIFICATION_EMAILS: Array.from(new Set(
    contactNotificationEmailSource
      .split(',')
      .map(email => email.trim())
      .filter(Boolean)
  )),
  WEBSITE_URL: (process.env.NEXT_PUBLIC_WEBSITE_URL || 'https://www.farzadbayat.com').trim(),
  DEFAULT_FROM_EMAIL: process.env.RESEND_FROM_EMAIL || 'F.B/c <contact@farzadbayat.com>',
  SCHEDULING: {
    USERNAME: schedulingUsername,
    EVENT: schedulingEvent,
    BOOKING_URL: `${schedulingBaseUrl}/${schedulingUsername}/${schedulingEvent}`,
    /** cal.com iframe embed (not app.cal.com — blank iframe in many browsers) */
    EMBED_URL: `${schedulingBaseUrl}/${schedulingUsername}/${schedulingEvent}?embed=true`,
    EMBED_SCRIPT_SRC: schedulingEmbedScript,
    BASE_URL: schedulingBaseUrl,
    EMBED_BASE_URL: schedulingEmbedBaseUrl,
  },
} as const

// Centralized contact details (phone, location, etc.)
export const CONTACT_DETAILS = [
  {
    label: 'Email',
    value: CONTACT_CONFIG.SUPPORT_EMAIL,
    href: `mailto:${CONTACT_CONFIG.SUPPORT_EMAIL}`,
  },
  {
    label: 'Phone',
    value: '+47 94 44 64 46',
    href: 'tel:+4794446446',
  },
  {
    label: 'Location',
    // Display text is localized: landing.json contact.location (nb/en).
    value: 'Oslo, Norway',
    href: null,
  },
] as const

// Voice System Configuration
export const VOICE_CONFIG = {
  BY_LANG: {
    'en-US': 'Puck',
    'en-GB': 'Puck',
    'nb-NO': 'Puck',
    'sv-SE': 'Puck',
    'de-DE': 'Puck',
    'es-ES': 'Puck',
  } as const,
  DEFAULT_VOICE: 'Puck',
  VISUAL_TRIGGERS: (
    process.env.LIVE_SERVER_VISUAL_TRIGGERS || 'screen,showing,look at,see this,dashboard,workflow'
  )
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean),
  VISUAL_INJECT_THROTTLE_MS: Math.max(
    0,
    Number.parseInt(process.env.LIVE_SERVER_VISUAL_INJECT_THROTTLE_MS || '8000', 10) || 8000
  ),
  CONTEXT_INJECT_DEBOUNCE_MS: Math.max(
    0,
    Number.parseInt(process.env.LIVE_SERVER_CONTEXT_INJECT_DEBOUNCE_MS || '5000', 10) || 5000
  ),
  INJECT_ON_CONTEXT_UPDATE: process.env.LIVE_SERVER_INJECT_ON_CONTEXT_UPDATE === '0' ? false : true,
} as const

// Calendar/Booking Configuration
// All booking links now use Cal.com
export const CALENDAR_CONFIG = {
  DEFAULT_LINK: CONTACT_CONFIG.SCHEDULING.BOOKING_URL,
  WORKSHOP_LINK: CONTACT_CONFIG.SCHEDULING.BOOKING_URL,
  CONSULTATION_LINK: CONTACT_CONFIG.SCHEDULING.BOOKING_URL,
  // Aliases for agent usage - all point to same Cal.com booking
  WORKSHOP: CONTACT_CONFIG.SCHEDULING.BOOKING_URL,
  CONSULTING: CONTACT_CONFIG.SCHEDULING.BOOKING_URL,
  DEFAULT: CONTACT_CONFIG.SCHEDULING.BOOKING_URL,
  // Function to get calendar link (all types use same Cal.com URL)
  getLink: (_type?: 'workshop' | 'consultation' | 'default'): string => {
    return CONTACT_CONFIG.SCHEDULING.BOOKING_URL
  },
} as const

// Gemini Configuration
export const GEMINI_CONFIG = {
  DEFAULT_TEMPERATURE: 1.0,
  MAX_TOKENS: 8192,
  SYSTEM_PROMPT: `You are F.B/c, Farzad Bayat's sharp, friendly consulting assistant.
- Speak concisely (2 sentences max by default).
- Ask one focused question when you need more context.
- Keep a natural voice tone; avoid lists unless asked.
- Vision: You only see webcam/screen frames when the user enabled them AND you received recent frames. If you haven't received frames, say you can't see it and ask them to enable sharing.
- Accuracy: Do not invent personal facts (company, role, location). Only use details the user provided or that you can cite from explicit sources; otherwise ask to confirm.
Pronunciation: "Farzad Bayat" ~ "Fahr–zahd Bye–yaht" (soft 'a' in Farzad).`,
} as const

// Feature Flags
export const FEATURE_FLAGS = {
  // Live Presence Canvas v2 — default on, env can still disable with 0/false.
  LIVE_PRESENCE_CANVAS_V2:
    (process.env.NEXT_PUBLIC_FEATURE_LIVE_PRESENCE_CANVAS_V2 || '1').toLowerCase() === '1' ||
    (process.env.NEXT_PUBLIC_FEATURE_LIVE_PRESENCE_CANVAS_V2 || '').toLowerCase() === 'true',
  // Controls whether the primary chat experience is the dedicated /live page
} as const

// Agent Stage Configuration - User-friendly stage descriptions
export const AGENT_STAGE_CONFIG = {
  DISCOVERY: {
    label: 'Discovery',
    description: 'Understanding your business needs and goals',
    order: 1,
  },
  SCORING: {
    label: 'Analysis',
    description: 'Evaluating solution fit for your situation',
    order: 2,
  },
  WORKSHOP_PITCH: {
    label: 'Workshop Solution',
    description: 'Designing training approach for your team',
    order: 3,
  },
  CONSULTING_PITCH: {
    label: 'Custom Solution',
    description: 'Architecting enterprise AI implementation',
    order: 3,
  },
  CLOSING: {
    label: 'Proposal',
    description: 'Finalizing next steps and addressing questions',
    order: 4,
  },
  SUMMARY: {
    label: 'Summary',
    description: 'Preparing your personalized strategy report',
    order: 5,
  },
} as const

// Agent UI Configuration (canonical)
export const AGENT_UI_CONFIG = {
  websocketUrl: WEBSOCKET_CONFIG.URL,
  features: {
    voice: true,
    video: true,
    screenShare: true,
    chat: true,
    transcripts: true,
  },
  reconnectAttempts: WEBSOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS,
  reconnectDelay: WEBSOCKET_CONFIG.RECONNECT_DELAY,
  isPreConnectBufferEnabled: true,
  agentName: 'fbc-agent',
  model: GEMINI_MODELS.DEFAULT_VOICE,
} as const

// Admin Configuration
export const ADMIN_CONFIG = {
  ADMIN_ID: 'farzad',
  ADMIN_EMAIL: 'farzad@bayatconsulting.com',
} as const

// ─── Gemini Live API Vision Streaming ───────────────────────────────
// Per official docs: max 1 frame per second via sendRealtimeInput({ video }).
// Both webcam and screen share must respect this limit.
export const FRAME_STREAM_INTERVAL_MS = 1000

// Gemini Live API model capability reference
// gemini-3.8-live turns proactive audio on by itself. Do not send proactiveAudio:false
// or enableAffectiveDialog — both error. Tools stay sequential because config-builder
// pins behavior BLOCKING on the cloned declarations.
export const GEMINI_LIVE_FEATURES = {
  supported: {
    sendClientContentForInitialHistory: true,
    visionViaRealtimeInput: true,
    nativeGoogleSearch: true,
    functionCalling: 'sequential' as const,
  },
  unsupported: {
    proactiveAudio: false,
    affectiveDialog: false,
  },
} as const

export const GENERIC_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'aol.com',
  'live.com',
  'mail.com',
  'yandex.com',
  'gmx.com',
  'zoho.com',
])

export const BLOCKED_EMAIL_DOMAINS = new Set([
  'example.com',
  'example.org',
  'example.net',
  'test.com',
  'test.org',
  'test.net',
  'invalid',
  'localhost',
  'mailinator.com',
  'tempmail.com',
  'guerrillamail.com',
  '10minutemail.com',
  'throwaway.email',
])
